import PropTypes from "prop-types";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const AREA_ROW_CONFIG = [
  { key: "aptitude", label: "Aptitude" },
  { key: "subject", label: "Subject" },
  { key: "other_preference", label: "Other Preferences" },
];

const MAX_OTHER_PREFERENCE_ITEMS = 6;
const CAREER_BUDDY_SUGGESTION = "Chat With Career Buddy";
const HIDE_WHEN_EMPTY_AREA_KEYS = new Set(["aptitude", "subject"]);
const EMPTY_VALUE_PLACEHOLDERS = new Set(["-", "na", "n/a", "none"]);

const styles = {
  wrapper: {
    mt: 7,
    px: { xs: 0, md: 1 },
  },
  panel: {
    borderRadius: "20px",
    backgroundColor: "#F3F4F8",
    border: "1px solid #E5E7EB",
    overflow: "hidden",
    position: "relative",
  },
  leftAccent: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 8,
    backgroundColor: "#F3A43B",
  },
  content: {
    pl: { xs: 2.5, md: 4 },
    pr: { xs: 2, md: 3.2 },
    py: { xs: 2.2, md: 2.8 },
  },
  intro: {
    fontSize: { xs: "14px", md: "17px" },
    color: "#2E314E",
    fontWeight: 500,
    lineHeight: 1.55,
    mb: 2,
  },
  highlightedCareer: {
    color: "#E09A30",
    fontWeight: 700,
  },
  divider: {
    border: "none",
    borderTop: "1px solid #D7D9E1",
    margin: "0 0 14px 0",
  },
  heading: {
    color: "#7A7F95",
    fontSize: { xs: "13px", md: "16px" },
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    mb: 1.4,
  },
  tableWrapper: {
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid #DDE1EA",
    backgroundColor: "#fff",
  },
  headCell: {
    backgroundColor: "#4A55C7",
    color: "#fff",
    fontWeight: 700,
    fontSize: "13px",
    py: 1,
    borderBottom: "none",
  },
  headAreaCell: {
    backgroundColor: "#1F2E92",
  },
  bodyCell: {
    borderBottom: "1px solid #E3E7F0",
    verticalAlign: "top",
    py: 1.1,
    px: 1.25,
    fontSize: "13px",
    color: "#23243A",
  },
  areaCell: {
    fontWeight: 700,
    color: "#1F2E92",
    minWidth: 116,
    width: 116,
  },
  areaMergedCell: {
    backgroundColor: "rgba(31,46,146,0.04)",
    borderRight: "1px solid #E2E7F4",
  },
  pairCell: {
    fontWeight: 500,
    lineHeight: 1.45,
  },
  groupInnerCell: {
    borderBottom: "1px dashed #D5DBE8",
  },
  rowAlt: {
    backgroundColor: "#F8FAFE",
  },
  progressSuggestion: {
    mt: 1.3,
    borderRadius: "10px",
    border: "1px solid #DDE3F1",
    backgroundColor: "#FAFBFF",
    px: 1.25,
    py: 1,
    display: "flex",
    alignItems: "center",
    gap: 0.6,
    flexWrap: "wrap",
  },
  progressLabel: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#566086",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  progressValue: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#2C3562",
  },
  ctaRow: {
    mt: 1.25,
    display: "flex",
    justifyContent: "flex-end",
  },
  progressButton: {
    borderRadius: "10px",
    px: 2.2,
    py: 0.85,
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.01em",
    color: "#fff",
    backgroundColor: "#F7AA47",
    boxShadow: "0 8px 18px rgba(247, 170, 71, 0.35)",
    border: "1px solid rgba(224, 146, 47, 0.85)",
    "&:hover": {
      backgroundColor: "#F39D30",
      boxShadow: "0 10px 22px rgba(247, 170, 71, 0.45)",
    },
  },
  emptyText: {
    color: "#848AA4",
  },
};

const toTitle = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const splitToList = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter((item) => item && !EMPTY_VALUE_PLACEHOLDERS.has(item.toLowerCase()));
  }

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item && !EMPTY_VALUE_PLACEHOLDERS.has(item.toLowerCase()));
};

const getRowValues = (careerInfo, key) => {
  if (key === "other_preference") {
    const improvement = [
      ...splitToList(careerInfo?.improvement_other),
      ...splitToList(careerInfo?.personality_improvement),
    ].slice(0, MAX_OTHER_PREFERENCE_ITEMS);
    const suggestion = [
      ...splitToList(careerInfo?.suggested_other_improvement),
      ...splitToList(
        careerInfo?.suggested_personality_improvement ||
          careerInfo?.suggested_personlaity_improvement
      ),
    ].slice(0, MAX_OTHER_PREFERENCE_ITEMS);

    return {
      improvement,
      suggestion,
    };
  }

  const backendMap = {
    aptitude: {
      improvement: careerInfo?.improvement_aptitude,
      suggestion: careerInfo?.suggested_aptitude_improvement,
    },
    subject: {
      improvement: careerInfo?.improvement_subject,
      suggestion: careerInfo?.suggested_subject_improvement,
    },
  };

  return {
    improvement: splitToList(backendMap[key]?.improvement),
    suggestion: splitToList(backendMap[key]?.suggestion),
  };
};

const hasImprovementEntries = ({ improvement = [] }) => improvement.length > 0;

const buildPairs = (improvements = [], suggestions = [], options = {}) => {
  const size = Math.max(improvements.length, suggestions.length, 1);
  const useCareerBuddyFallback = Boolean(options.useCareerBuddyFallback);

  return Array.from({ length: size }, (_, index) => ({
    improvement: improvements[index] || "",
    suggestion:
      suggestions[index] ||
      (useCareerBuddyFallback && improvements[index]
        ? CAREER_BUDDY_SUGGESTION
        : ""),
  }));
};

const getSkillPlanAreas = (careerInfo) =>
  AREA_ROW_CONFIG.map((row) => {
    const rowValues = getRowValues(careerInfo, row.key);
    const shouldHideEmptyRow =
      HIDE_WHEN_EMPTY_AREA_KEYS.has(row.key) &&
      !hasImprovementEntries(rowValues);

    if (shouldHideEmptyRow) {
      return null;
    }

    return {
      ...row,
      pairs: buildPairs(rowValues.improvement, rowValues.suggestion, {
        useCareerBuddyFallback: HIDE_WHEN_EMPTY_AREA_KEYS.has(row.key),
      }),
    };
  }).filter(Boolean);

const highlightCareerInText = (text, selectedName) => {
  if (!selectedName || !text.includes(selectedName)) {
    return text;
  }

  const [before, ...afterParts] = text.split(selectedName);
  return (
    <>
      {before}
      <Box component="span" sx={styles.highlightedCareer}>
        {selectedName}
      </Box>
      {afterParts.join(selectedName)}
    </>
  );
};

const MilestoneLayout = ({ selectedCareer }) => {
  const navigate = useNavigate();
  const selectedName = toTitle(
    selectedCareer?.raw?.career_choice || selectedCareer?.title || ""
  );
  const careerInfo = selectedCareer?.raw?.career_info || {};
  const skillPlanAreas = getSkillPlanAreas(careerInfo);
  const introText = `Your personality and strengths show a strong natural match with ${selectedName}. Here are a few exciting areas where a little effort will help you truly shine. Start early and build your best self.`;

  return (
    <Box sx={styles.wrapper}>
      <Paper sx={styles.panel}>
        <Box sx={styles.leftAccent} />
        <Box sx={styles.content}>
          <Typography sx={styles.intro}>
            {highlightCareerInText(introText, selectedName)}
          </Typography>

          <hr style={styles.divider} />

          <Typography sx={styles.heading}>Your Skill Growth Plan</Typography>

          <TableContainer sx={styles.tableWrapper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...styles.headCell, ...styles.headAreaCell }}>
                    Area
                  </TableCell>
                  <TableCell sx={styles.headCell}>Areas of Improvement</TableCell>
                  <TableCell sx={styles.headCell}>Suggestion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skillPlanAreas.map((row, areaIndex) => {
                  const { pairs } = row;
                  const isAltArea = areaIndex % 2 === 1;

                  return pairs.map((pair, pairIndex) => {
                    const isLastInArea = pairIndex === pairs.length - 1;

                    return (
                      <TableRow
                        key={`${row.key}-${pairIndex}`}
                        sx={isAltArea ? styles.rowAlt : undefined}
                      >
                        {pairIndex === 0 && (
                          <TableCell
                            rowSpan={pairs.length}
                            sx={{
                              ...styles.bodyCell,
                              ...styles.areaCell,
                              ...styles.areaMergedCell,
                            }}
                          >
                            {row.label}
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            ...styles.bodyCell,
                            ...styles.pairCell,
                            ...(isLastInArea ? null : styles.groupInnerCell),
                          }}
                        >
                          {pair.improvement ? (
                            toTitle(pair.improvement)
                          ) : (
                            <Typography component="span" sx={styles.emptyText}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.bodyCell,
                            ...styles.pairCell,
                            ...(isLastInArea ? null : styles.groupInnerCell),
                          }}
                        >
                          {pair.suggestion ? (
                            toTitle(pair.suggestion)
                          ) : (
                            <Typography component="span" sx={styles.emptyText}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* <Box sx={styles.progressSuggestion}>
            <Typography component="span" sx={styles.progressLabel}>
              Overall Career Progress:
            </Typography>
            <Typography component="span" sx={styles.progressValue}>
              {careerOptionNumber
                ? `Career ${careerOptionNumber}: Talk to Career Counsellor`
                : "Talk to Career Counsellor"}
            </Typography>
          </Box> */}

          <Box sx={styles.ctaRow}>
            <Button
              variant="contained"
              sx={styles.progressButton}
              onClick={() => navigate("/milestone")}
            >
              Track Progress
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

MilestoneLayout.propTypes = {
  selectedCareer: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    raw: PropTypes.shape({
      priority: PropTypes.number,
      career_choice: PropTypes.string,
      career_info: PropTypes.object,
    }),
  }),
};

export default MilestoneLayout;
